import React, { useState, useEffect, useRef } from "react";

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

const PortfolioSequence = () => {
  const [posts, setPosts] = useState([]);
  const [categories] = useState([
    "ai-ml",
    "iot",
    "unity",
    "ar",
    "web",
    "mobile"
  ]);
  const [selectedPost, setSelectedPost] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sequenceNumber, setSequenceNumber] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", success: false });
  const [tableSearch, setTableSearch] = useState("");
  const [tableCategoryFilter, setTableCategoryFilter] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/portfolio/getposts`);
        const data = await res.json();
        setPosts(Array.isArray(data) ? data : []);
      } catch (err) {
        setPosts([]);
      }
    };
    fetchPosts();
  }, []);

  const handleSubmit = async () => {
    const payload = {
      category: selectedCategory,
      postId: selectedPost,
      newSequenceNumber: sequenceNumber
    };
    try {
      const res = await fetch(`${BASE_URL}/api/portfolio/update-category-sequence`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      setToast({
        show: true,
        message: result.message || "Something happened.",
        success: result.success
      });
      if (result.success) {
        setSelectedPost("");
        setSelectedCategory("");
        setSequenceNumber("");
        setSearchTerm("");
      }
      setTimeout(() => setToast({ show: false, message: "", success: false }), 3000);
    } catch (err) {
      setToast({
        show: true,
        message: "Error updating sequence.",
        success: false
      });
      setTimeout(() => setToast({ show: false, message: "", success: false }), 3000);
    }
  };

  const handleNumberChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setSequenceNumber(value);
  };

  const filteredPosts = posts.filter((post) =>
    post.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedPostName = posts.find((p) => p._id === selectedPost)?.name || "";

  // Filter posts for table by selected category and table search
  const tablePosts = posts.filter(post =>
    (!tableCategoryFilter || post.category.includes(tableCategoryFilter)) &&
    post.name.toLowerCase().includes(tableSearch.toLowerCase())
  );

  // Updated styles for consistency
  const styles = {
    pageContainer: {
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      padding: '32px'
    },
    splitContainer: {
      display: 'flex',
      gap: '32px',
      maxWidth: '1200px',
      margin: '0 auto',
      alignItems: 'flex-start'
    },
    formSection: {
      flex: '1',
      maxWidth: '440px',
      background: '#fff',
      padding: '32px',
      borderRadius: '14px',
      boxShadow: '0 3px 20px rgba(0,0,0,0.06)',
      border: '1px solid #e5e7eb'
    },
    tableSection: {
      flex: '1.2',
      background: '#fff',
      borderRadius: '14px',
      boxShadow: '0 3px 20px rgba(0,0,0,0.06)',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '2rem',
      color: '#1e3a8a',
      textAlign: 'left',
      letterSpacing: '-0.5px',
      fontFamily: 'inherit'
    },
    tableTitle: {
      fontSize: '1.35rem',
      fontWeight: '700',
      padding: '24px 24px 16px',
      color: '#1e3a8a',
      textAlign: 'left',
      letterSpacing: '-0.3px',
      borderBottom: '1px solid #f0f0f0',
      fontFamily: 'inherit'
    },
    formGroup: {
      marginBottom: '24px',
      position: 'relative'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '8px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontFamily: 'inherit'
    },
    input: {
      width: '100%',
      padding: '0.65rem 0.85rem',
      fontSize: '16px',
      border: '1px solid #d0d5dd',
      borderRadius: '6px',
      backgroundColor: '#fafbfc',
      color: '#1e3a8a',
      outline: 'none',
      transition: 'border-color 0.2s',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1000,
      background: '#fff',
      border: '1px solid #d0d5dd',
      borderRadius: '6px',
      marginTop: '8px',
      maxHeight: '220px',
      overflowY: 'auto',
      boxShadow: '0 10px 40px rgba(0,0,0,0.12)'
    },
    dropdownItem: {
      padding: '0.65rem 0.85rem',
      fontSize: '16px',
      color: '#1e3a8a',
      cursor: 'pointer',
      transition: 'background 0.2s',
      borderBottom: '1px solid #f3f4f6'
    },
    emptyState: {
      padding: '18px',
      textAlign: 'center',
      color: '#9ca3af',
      fontSize: '15px'
    },
    button: {
      width: '100%',
      padding: '14px',
      fontSize: '17px',
      fontWeight: '600',
      color: '#fff',
      background: 'linear-gradient(90deg, #1e3a8a, #2563eb)',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.25s ease',
      marginTop: '8px',
      boxShadow: '0 4px 14px rgba(37,99,235,0.12)',
      fontFamily: 'inherit'
    },
    buttonDisabled: {
      background: '#e5e7eb',
      color: '#9ca3af',
      cursor: 'not-allowed',
      boxShadow: 'none'
    },
    searchInput: {
      width: '100%',
      padding: '0.65rem 0.85rem',
      fontSize: '16px',
      border: '1px solid #d0d5dd',
      borderRadius: '6px',
      background: '#fafbfc',
      outline: 'none',
      transition: 'border-color 0.2s',
      fontFamily: 'inherit'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.95rem',
      fontFamily: 'inherit'
    },
    tableHeader: {
      background: '#f0f7ff',
      borderBottom: '1px solid #e5e7eb'
    },
    th: {
      textAlign: 'left',
      padding: '0.875rem 1rem',
      color: '#222',
      fontWeight: '600',
      fontSize: '0.95rem',
      textTransform: 'uppercase',
      letterSpacing: '0.025em',
      fontFamily: 'inherit'
    },
    td: {
      padding: '1rem',
      fontSize: '1rem',
      verticalAlign: 'middle',
      color: '#222',
      fontWeight: '500',
      fontFamily: 'inherit'
    },
    tableRow: {
      transition: 'background-color 0.2s ease'
    },
    toast: {
      position: "fixed",
      top: "32px",
      right: "32px",
      padding: "16px 28px",
      borderRadius: "12px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
      zIndex: 9999,
      fontSize: "15px",
      fontWeight: '600',
      color: '#fff',
      animation: 'slideIn 0.3s ease'
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div style={styles.pageContainer}>
      <div style={styles.splitContainer}>
        {/* Left: Form */}
        <div style={styles.formSection}>
          {toast.show && (
            <div
              style={{
                ...styles.toast,
                background: toast.success 
                  ? 'linear-gradient(135deg, #10b981, #059669)' 
                  : 'linear-gradient(135deg, #ef4444, #dc2626)'
              }}
            >
              {toast.message}
            </div>
          )}

          <h3 style={styles.title}>Change Portfolio Sequence</h3>

          <div style={styles.formGroup} ref={dropdownRef}>
            <label style={styles.label}>Portfolio Post</label>
            <input
              type="text"
              style={styles.input}
              placeholder="Search post..."
              value={selectedPost ? selectedPostName : searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSelectedPost("");
                setIsDropdownOpen(true);
              }}
              onFocus={() => setIsDropdownOpen(true)}
              autoComplete="off"
            />
            {isDropdownOpen && (
              <div style={styles.dropdown}>
                {filteredPosts.length > 0 ? (
                  filteredPosts.map((post) => (
                    <div
                      key={post._id}
                      style={styles.dropdownItem}
                      onClick={() => {
                        setSelectedPost(post._id);
                        setSearchTerm("");
                        setIsDropdownOpen(false);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e8efff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {post.name}
                    </div>
                  ))
                ) : (
                  <div style={styles.emptyState}>No posts found</div>
                )}
              </div>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Category</label>
            <select
              style={styles.input}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.split('-').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Sequence</label>
            <input
              type="text"
              style={styles.input}
              placeholder="Enter number"
              value={sequenceNumber}
              onChange={handleNumberChange}
              inputMode="numeric"
              pattern="[0-9]*"
            />
          </div>

          <button
            style={{
              ...styles.button,
              ...(!selectedPost || !selectedCategory || !sequenceNumber ? styles.buttonDisabled : {})
            }}
            disabled={!selectedPost || !selectedCategory || !sequenceNumber}
            onClick={handleSubmit}
          >
            Save Changes
          </button>
        </div>

        {/* Right: Table */}
        <div style={styles.tableSection}>
          <div style={styles.tableTitle}>Posts & Sequences</div>
          
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', margin: '0 24px 18px 24px' }}>
            <select
              style={{
                ...styles.input,
                width: '220px',
                padding: '0.65rem 0.85rem',
                fontSize: '16px',
                margin: 0,
                background: '#fafbfc'
              }}
              value={tableCategoryFilter}
              onChange={e => setTableCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat.split('-').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Search posts..."
              value={tableSearch}
              onChange={e => setTableSearch(e.target.value)}
              style={{ ...styles.searchInput, margin: 0, width: '100%' }}
            />
          </div>
          
          <table style={styles.table}>
            <thead style={styles.tableHeader}>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Sequence</th>
              </tr>
            </thead>
            <tbody>
              {tablePosts.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af', padding: '32px' }}>
                    No posts found
                  </td>
                </tr>
              ) : (
                tablePosts.map(post => (
                  <tr 
                    key={post._id} 
                    style={styles.tableRow}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={styles.td}>{post.name}</td>
                    <td style={styles.td}>
                      {Array.isArray(post.category)
                        ? post.category.map(cat =>
                            cat.split('-').map(word =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ')
                          ).join(', ')
                        : '-'}
                    </td>
                    <td style={styles.td}>
                      {Array.isArray(post.category)
                        ? post.category.map(cat => {
                            const seq = post.sequenceNumber && post.sequenceNumber[cat];
                            const catName = cat.split('-').map(word =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ');
                            return `${catName}: ${seq !== undefined ? seq : '-'}`;
                          }).join(', ')
                        : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default PortfolioSequence;